import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { t } from 'i18next';
import moment from 'moment';
import React, { useState, useRef } from 'react';
import { FaEdit } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { useToast } from '../../core/contexts/toast';
import { formatarPorcentagem, formatLargeNumber, isNumeric } from '../../core/utils/funcoes';
import { ModeloSopFinance } from '../../core/models/sopFinance';
import { RepositorioSopFinance } from '../../core/repositories/sopFinanceRepositorio';

interface Props {
  lista: ModeloSopFinance[];
  setLista: React.Dispatch<React.SetStateAction<ModeloSopFinance[]>>;
}



const SopFinanceTable: React.FC<Props> = (props) => {
  const gridApi = useRef<any>(null);
  const { mostrarToast } = useToast();
  const [loading, setLoading] = useState(false);

  const columnDefs: ColDef<ModeloSopFinance>[] = [
    { headerName: 'Business Unit', field: 'des_business_unit', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Month/Year', field: 'des_time', minWidth: 100 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={true} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Product', field: 'des_product', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Selling Price Real', field: 'num_selling_price_real', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Selling Price Forecast', field: 'num_selling_price_forecast', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Selling Price Budget', field: 'num_selling_price_budget', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Unit COGS Real', field: 'num_unit_cogs_real', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Unit COGS Forecast', field: 'num_unit_cogs_forecast', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Unit COGS Budget', field: 'num_unit_cogs_budget', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} date={false} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Gross Margin Real', field: 'num_gross_margin_real', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} money porcetagem attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Gross Margin Forecast', field: 'num_gross_margin_forecast', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} money porcetagem attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Gross Margin Budget', field: 'num_gross_margin_budget', minWidth: 120 , cellRenderer: (params: any) => <CustomSkeleton value={params.value} money porcetagem attr={params.colDef.field} linha={params.data} /> },
  ];

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    gridApi.current.sizeColumnsToFit();
  };

  const CustomSkeleton = (props: { value: any; money?: boolean; date?: boolean; porcetagem?: boolean, attr?: string, linha?: ModeloSopFinance }) => {
    return (
      <>
        {loading ? (
          <Skeleton className="width-100" />
        ) : (
          <label className='text-capitalize'>
            {(localStorage.getItem("editando") && (typeof props.value === "number" || props.date)) && (
              <label onClick={() => { atualizarValor(props.attr || "", props.linha || new ModeloSopFinance()) }} className="cursor-pointer">&nbsp;<FaEdit /></label>
            )}
            {props.value ? props.date ? (
              moment(props.value).format("MMM-YYYY")
            ) : typeof props.value === "number" ? (
              props.porcetagem ?
                formatarPorcentagem(props.value / 100, 2)
                : formatLargeNumber(props.value, props.money ? t("components.R$") : "")
            ) : (
              props.value
            ) : "-"}
          </label>
        )}
      </>
    );
  };

  async function atualizarValor(atributo: string, linha: ModeloSopFinance) {
    const valor = prompt(`Por favor, insira um valor ${atributo} (${(linha as any)[atributo]}):`);
    if (valor) {
      const _centro = new RepositorioSopFinance();
      _centro.atualizarValor(atributo, valor, linha).then((atualizadas) => {
        if (atualizadas) {
          var encontra = props.lista.find(x => x.cod_tela_sop_finance === linha.cod_tela_sop_finance);
          if (encontra) (encontra as any)[atributo] = isNumeric(valor) ? Number.parseFloat(valor) : valor;
          props.setLista([...props.lista]);
        }
        mostrarToast({
          titulo: "Sucesso",
          mensagem: `${atualizadas} linha(s) atualizada(s)`,
          tipo: "success",
        });
      }).catch(err => {
        mostrarToast({
          titulo: "Erro",
          mensagem: t("pages.Aconteceu algo inesperado, por favor tente novamente mais tarde!"),
          tipo: "error",
        });
      });
    }
  }

  return (
    <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
      <AgGridReact<ModeloSopFinance>
        rowData={props.lista}
        columnDefs={columnDefs}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
          headerClass: 'wrap-header',
          cellClass: 'numeric-cell'
        }}
        pagination={true}
        paginationPageSize={20}
        domLayout="autoHeight"
        onGridReady={onGridReady}
      />
    </div>
  );
};

export default SopFinanceTable;
